import type { LayoutItem } from 'react-grid-layout';
import type { PanelDefinition } from './panelCatalog';

export const DASHBOARD_COLS = 12;

// Pure: compute a non-overlapping default layout by flowing panels left to
// right across `cols` columns, wrapping to a new shelf (row) when the next
// panel does not fit on the current one. Each new shelf starts below the
// tallest panel of the previous shelf, so panels never stack on top of each
// other. Deterministic for a given panel order.
export const packLayout = (
  panels: PanelDefinition[],
  cols: number = DASHBOARD_COLS
): LayoutItem[] => {
  const items: LayoutItem[] = [];
  let cursorX = 0;
  let shelfY = 0;
  let shelfHeight = 0;

  for (const panel of panels) {
    const { w, h } = panel.defaultLayout;
    const width = Math.min(w, cols);

    // Wrap to a new shelf when this panel would overflow the row.
    if (cursorX + width > cols) {
      shelfY += shelfHeight;
      cursorX = 0;
      shelfHeight = 0;
    }

    items.push({
      ...panel.defaultLayout,
      i: panel.key,
      x: cursorX,
      y: shelfY,
      w: width,
    });

    cursorX += width;
    shelfHeight = Math.max(shelfHeight, h);
  }

  return items;
};

// Pure: place one new panel onto an existing layout. Tries to fit it on the
// last shelf (to the right of the rightmost panel on the bottom row); if it
// would overflow `cols`, it starts a fresh shelf below everything. Mirrors the
// flow of `packLayout` so a toggled-on panel lands where packing would put it.
export const placePanel = (
  layout: LayoutItem[],
  panel: PanelDefinition,
  cols: number = DASHBOARD_COLS
): LayoutItem => {
  const width = Math.min(panel.defaultLayout.w, cols);
  const base = { ...panel.defaultLayout, i: panel.key, w: width };

  if (layout.length === 0) {
    return { ...base, x: 0, y: 0 };
  }

  // The bottom shelf is everything sharing the largest y; fill it rightward.
  const bottomY = layout.reduce((max, l) => Math.max(max, l.y), 0);
  const shelf = layout.filter((l) => l.y === bottomY);
  const shelfRight = shelf.reduce((max, l) => Math.max(max, l.x + l.w), 0);

  if (shelfRight + width <= cols) {
    return { ...base, x: shelfRight, y: bottomY };
  }

  // Otherwise drop to a new shelf below every existing panel.
  const stackBottom = layout.reduce((max, l) => Math.max(max, l.y + l.h), 0);
  return { ...base, x: 0, y: stackBottom };
};
